import { MontantCalculDirective } from '../../directives/montant-calcul.directive';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  computed,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import {
  ComptePowensDisponible,
  ComptesService,
  ModificationSelectionComptesPowens,
  OperationImportPowens,
  SelectionComptesPowens,
} from '../../services/comptes.service';
import { PopupPointagePlanifieeComponent, PointageConfirmPayload, AjoutOperationProvisionPayload } from '../shared/popup-pointage-planifiee/popup-pointage-planifiee.component';
import { PopupEditionPlanifieeComponent, EditionPlanifieePayload } from '../shared/popup-edition-planifiee/popup-edition-planifiee.component';
import { PopupImportPlanifieeComponent, LigneImportee, AssociationConfirmee } from '../shared/popup-import-planifiee/popup-import-planifiee.component';
import { PopupSelectionPowensComponent } from '../shared/popup-selection-powens/popup-selection-powens.component';
import { PopupRechercheOperationComponent } from '../shared/popup-recherche-operation/popup-recherche-operation.component';
import { Compte } from '../../models/compte.model';
import { Categorie } from '../../models/categorie.model';
import {
  CreationOperationPlanifiee,
  OperationTableau,
  TableauPrevisionnel,
  TypeOperation,
} from '../../models/operation-planifiee.model';
import { CreationOperationReelle, OperationReelle } from '../../models/operation-reelle.model';
import { filtrerOperationsReellesParMois, totalOperationsReelles as totalOpsReelles } from '../../utils/operations.utils';
import { firstValueFrom } from 'rxjs';

interface EditionForm {
  libelle: string;
  type: TypeOperation;
  montant: number;
  dateDebut: string;
  jourDuMois: number;
  nombreDeMois: number | '';
  estEtalee: boolean;
  provision: boolean;
  categorie: string;
  moyenPaiement: string | null;
  numeroCheque: string;
  dateOperation: string;
  periodicite: number;
}

interface DiagnosticErreurPowens {
  statutHttpPowens?: number;
  codePowens?: string | null;
  propriete?: string | null;
  identifiantRequete?: string | null;
  endpointPowens?: string | null;
  conseil?: string;
}

interface PayloadErreurPowens {
  message?: unknown;
  comptesDisponibles?: ComptePowensDisponible[];
  diagnostic?: DiagnosticErreurPowens;
}

const NOMS_MOIS = [
  'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
  'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre',
];

const MOYENS_PAIEMENT = [
  { label: 'Prélèvement', value: 'PRELEVEMENT' },
  { label: 'CB', value: 'CB' },
  { label: 'CB différé', value: 'CB_DIFFERE' },
  { label: 'Chèque', value: 'CHEQUE' },
  { label: 'Virement', value: 'VIREMENT' },
];

@Component({
    selector: 'app-plan-annuel',
    imports: [
        CommonModule,
        FormsModule,
        MontantCalculDirective,
        PopupPointagePlanifieeComponent,
        PopupEditionPlanifieeComponent,
        PopupImportPlanifieeComponent,
        PopupSelectionPowensComponent,
        PopupRechercheOperationComponent,
    ],
    templateUrl: './plan-annuel.component.html',
    styleUrl: './plan-annuel.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class PlanAnnuelComponent implements OnChanges, OnInit, OnDestroy {
  @Input() compte: Compte | null = null;
  @Input() categories: Categorie[] = [];
  @Input() comptesDisponibles: Compte[] = [];
  @Input() refreshToken = 0;
  readonly rechercheVisible = signal(false);
  private mediaTelephone: MediaQueryList | null = null;
  private readonly synchroniserVueTelephone = (): void => {
    if (!this.mediaTelephone?.matches) return;
    this.moisAffiche.set(this.moisCourant);
    if (this.annee() === this.anneeCourante) return;
    this.annee.set(this.anneeCourante);
    this.celluleEditeeKey.set(null);
    this.annulerPointage();
    void this.charger();
  };
  @Output() ouvrirCreation = new EventEmitter<void>();
  @Output() operationModifiee = new EventEmitter<void>();
  @Output() ajouterCategorie = new EventEmitter<void>();

  async importerDepuisPressePapier(): Promise<void> {
    try {
      const texte = await navigator.clipboard.readText();
      if (!texte?.trim()) {
        this.erreur.set('Le presse-papier est vide.');
        return;
      }
      const toutesLignes = this.parserTSV(texte);
      if (toutesLignes.length === 0) {
        this.erreur.set('Aucune opération reconnue pour le mois courant ou précédent (format attendu : TSV avec colonnes Date, Libelle, Montant).');
        return;
      }
      this.ouvrirPopupImport(toutesLignes, 'presse-papier');
    } catch {
      this.erreur.set('Impossible de lire le presse-papier. Vérifiez les permissions du navigateur.');
    }
  }

  async importerDepuisPowens(): Promise<void> {
    if (!this.compte || this.chargementPowens() || this.chargementGestionPowens()) return;

    this.chargementPowens.set(true);
    this.erreur.set(null);
    try {
      const reponse = await firstValueFrom(
        this.comptesService.importerOperationsPowens(
          this.compte.id,
          this.anneeCourante,
          this.moisCourant,
        ),
      );

      if (reponse.connexionRequise) {
        window.location.assign(reponse.urlConnexion);
        return;
      }

      const lignes = reponse.operations
        .map((operation) => this.convertirOperationPowens(operation))
        .filter((ligne): ligne is LigneImportee => ligne !== null);

      if (lignes.length === 0) {
        this.erreur.set(`Aucune opération Powens trouvée pour ${reponse.comptePowens.nom} sur le mois courant ou parmi les cartes différées à venir.`);
        return;
      }

      const nombreCartes = reponse.comptesCartePowens?.length ?? 0;
      const suffixeCartes = nombreCartes
        ? ` + ${nombreCartes} carte(s)`
        : '';
      this.ouvrirPopupImport(
        lignes,
        `Powens — ${reponse.comptePowens.nom}${suffixeCartes}`,
      );
    } catch (error: unknown) {
      const comptesDisponibles = this.comptesDepuisErreurSelection(error);
      if (comptesDisponibles) {
        await this.ouvrirSelectionComptesPowens(comptesDisponibles);
      } else {
        this.erreur.set(this.messageErreurPowens(error));
      }
    } finally {
      this.chargementPowens.set(false);
    }
  }

  async ouvrirSelectionComptesPowens(
    comptesConnus?: ComptePowensDisponible[],
  ): Promise<void> {
    if (!this.compte || this.chargementSelectionPowens()) return;

    if (comptesConnus) this.comptesSelectionPowens.set(comptesConnus);
    this.selectionPowensVisible.set(true);
    this.chargementSelectionPowens.set(true);
    this.erreurSelectionPowens.set(null);
    this.erreur.set(null);
    try {
      const selection = await firstValueFrom(
        this.comptesService.obtenirSelectionComptesPowens(this.compte.id),
      );
      this.appliquerSelectionPowens(selection);
    } catch (error: unknown) {
      this.erreurSelectionPowens.set(this.messageErreurPowens(error));
    } finally {
      this.chargementSelectionPowens.set(false);
    }
  }

  async enregistrerSelectionComptesPowens(
    selection: ModificationSelectionComptesPowens,
  ): Promise<void> {
    if (!this.compte || this.chargementSelectionPowens()) return;

    this.chargementSelectionPowens.set(true);
    this.erreurSelectionPowens.set(null);
    let importApresEnregistrement = false;
    try {
      const reponse = await firstValueFrom(
        this.comptesService.enregistrerSelectionComptesPowens(
          this.compte.id,
          selection,
        ),
      );
      this.appliquerSelectionPowens(reponse);
      this.selectionPowensVisible.set(false);
      importApresEnregistrement = true;
    } catch (error: unknown) {
      this.erreurSelectionPowens.set(this.messageErreurPowens(error));
    } finally {
      this.chargementSelectionPowens.set(false);
    }

    if (importApresEnregistrement) await this.importerDepuisPowens();
  }

  fermerSelectionComptesPowens(): void {
    if (!this.chargementSelectionPowens()) {
      this.selectionPowensVisible.set(false);
      this.erreurSelectionPowens.set(null);
    }
  }

  private appliquerSelectionPowens(selection: SelectionComptesPowens): void {
    this.comptesSelectionPowens.set(selection.comptesDisponibles);
    this.comptePrincipalSelectionPowensId.set(selection.comptePrincipalId);
    this.comptesCarteSelectionPowensIds.set(selection.comptesCarteIds);
  }

  private comptesDepuisErreurSelection(
    error: unknown,
  ): ComptePowensDisponible[] | null {
    if (!error || typeof error !== 'object') return null;
    const payload = (error as { error?: unknown }).error;
    if (!payload || typeof payload !== 'object' || Array.isArray(payload)) {
      return null;
    }
    const comptes = (payload as PayloadErreurPowens).comptesDisponibles;
    return Array.isArray(comptes) ? comptes : null;
  }

  async gererComptesPowens(): Promise<void> {
    if (
      this.chargementPowens() ||
      this.chargementGestionPowens() ||
      this.chargementSelectionPowens()
    ) return;

    this.chargementGestionPowens.set(true);
    this.erreur.set(null);
    try {
      const reponse = await firstValueFrom(
        this.comptesService.ouvrirGestionPowens(),
      );
      window.location.assign(reponse.urlGestion);
    } catch (error: unknown) {
      const message = this.messageErreurPowens(error);
      this.erreur.set(message);
      if (this.selectionPowensVisible()) {
        this.erreurSelectionPowens.set(message);
      }
    } finally {
      this.chargementGestionPowens.set(false);
    }
  }

  private ouvrirPopupImport(toutesLignes: LigneImportee[], source: string): void {
    const lignesMoisCourant = toutesLignes.filter(
      (ligne) =>
        ligne.date.getFullYear() === this.anneeCourante &&
        ligne.date.getMonth() + 1 === this.moisCourant,
    );
    const moisPrecedent = new Date(this.anneeCourante, this.moisCourant - 2, 1);
    const lignesMoisPrecedent = toutesLignes.filter(
      (ligne) =>
        ligne.date.getFullYear() === moisPrecedent.getFullYear() &&
        ligne.date.getMonth() === moisPrecedent.getMonth(),
    );
    const debutMoisSuivant = new Date(
      this.anneeCourante,
      this.moisCourant,
      1,
    );
    const debutMoisApresSuivant = new Date(
      this.anneeCourante,
      this.moisCourant + 1,
      1,
    );
    const cartesDiffereesMoisSuivant = toutesLignes.filter(
      (ligne) =>
        this.estCarteDiffereeAVenir(ligne) &&
        ligne.date >= debutMoisSuivant &&
        ligne.date < debutMoisApresSuivant,
    );
    const estImportPowens = source.startsWith('Powens');
    const lignesBase = lignesMoisCourant.length > 0 || estImportPowens
      ? lignesMoisCourant
      : lignesMoisPrecedent;
    const lignes = [...lignesBase, ...cartesDiffereesMoisSuivant];

    if (lignes.length === 0) {
      this.erreur.set(
        estImportPowens
          ? 'Aucune opération Powens reconnue pour le mois courant ou parmi les cartes différées à venir.'
          : 'Aucune opération reconnue pour le mois courant ou précédent.',
      );
      return;
    }

    this.importSource.set(source);
    this.importToutesLignes.set(toutesLignes);
    this.importLignes.set(lignes);
  }

  private estCarteDiffereeAVenir(ligne: LigneImportee): boolean {
    const statut = ligne.statut.trim().toLocaleLowerCase('fr-FR');
    const type = ligne.typeLabel.trim().toLocaleLowerCase('fr-FR');
    return (
      statut === 'à venir' &&
      (ligne.moyenPaiementDetecte === 'CB_DIFFERE' ||
        type.includes('différ') ||
        type.includes('differ'))
    );
  }

  private convertirOperationPowens(operation: OperationImportPowens): LigneImportee | null {
    const date = this.parseDateISO(operation.dateOperation);
    if (!date) return null;
    const dateValeur = this.parseDateISO(operation.dateValeur);

    return {
      dateStr: this.formaterDateImport(date),
      date,
      dateValeurStr: dateValeur ? this.formaterDateImport(dateValeur) : null,
      dateValeur,
      libelle: operation.libelle,
      lieu: '',
      categorie: operation.categorie,
      typeLabel: operation.typeLabel,
      statut: operation.statut,
      montantBrut: operation.montant,
      typeOp: operation.montant >= 0 ? 'ENTREE' : 'SORTIE',
      moyenPaiementDetecte: operation.moyenPaiement,
      numeroCheque: operation.numeroCheque,
      referenceImport: operation.referenceImport,
    };
  }

  private parseDateISO(valeur: string | null | undefined): Date | null {
    if (!valeur) return null;
    const morceaux = valeur.split('-').map(Number);
    if (morceaux.length !== 3) return null;
    const [annee, mois, jour] = morceaux;
    if (!annee || !mois || !jour) return null;

    const date = new Date(annee, mois - 1, jour);
    return !Number.isNaN(date.getTime()) &&
      date.getFullYear() === annee &&
      date.getMonth() === mois - 1 &&
      date.getDate() === jour
      ? date
      : null;
  }

  private formaterDateImport(date: Date): string {
    return `${String(date.getDate()).padStart(2, '0')}/${String(date.getMonth() + 1).padStart(2, '0')}/${date.getFullYear()}`;
  }

  private formaterDateISO(date: Date): string {
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
  }

  private messageErreurGenerique(error: unknown, fallback: string): string {
    if (error && typeof error === 'object') {
      const payload = (error as { error?: { message?: unknown } }).error;
      if (payload && typeof payload.message === 'string' && payload.message.trim()) {
        return payload.message;
      }
    }
    return fallback;
  }

  private messageErreurPowens(error: unknown): string {
    if (!error || typeof error !== 'object') {
      return 'Impossible de récupérer les opérations depuis Powens.';
    }

    const erreurHttp = error as { error?: unknown; message?: unknown; status?: number };
    const payloadBrut = erreurHttp.error;
    if (payloadBrut && typeof payloadBrut === 'object' && !Array.isArray(payloadBrut)) {
      const payload = payloadBrut as PayloadErreurPowens;
      const message = this.texteErreur(payload.message);
      if (message) {
        const complements: string[] = [];
        const comptes = payload.comptesDisponibles
          ?.map((compte) => {
            const details = [
              compte.type,
              compte.numeroMasque,
              compte.compteParentId ? `rattaché au ${compte.compteParentId}` : null,
              compte.solde !== null && compte.solde !== undefined
                ? `solde ${compte.solde.toLocaleString('fr-FR')} €`
                : null,
              compte.aVenir !== null && compte.aVenir !== undefined
                ? `à venir ${compte.aVenir.toLocaleString('fr-FR')} €`
                : null,
            ].filter((detail): detail is string => Boolean(detail));
            return `${compte.nom} (${compte.id}${details.length ? ` — ${details.join(', ')}` : ''})`;
          })
          .join(', ');
        if (comptes) complements.push(`Comptes disponibles : ${comptes}.`);

        const diagnostic = payload.diagnostic;
        if (diagnostic) {
          const contexte = [
            diagnostic.statutHttpPowens
              ? `HTTP Powens ${diagnostic.statutHttpPowens}`
              : null,
            diagnostic.codePowens ? `code ${diagnostic.codePowens}` : null,
            diagnostic.endpointPowens
              ? `appel ${diagnostic.endpointPowens}`
              : null,
            diagnostic.propriete ? `champ ${diagnostic.propriete}` : null,
            diagnostic.identifiantRequete
              ? `requête ${diagnostic.identifiantRequete}`
              : null,
          ].filter((element): element is string => Boolean(element));
          if (contexte.length > 0) complements.push(`Détail : ${contexte.join(', ')}.`);
          if (diagnostic.conseil) complements.push(`Conseil : ${diagnostic.conseil}`);
        }

        return [message, ...complements].join(' ');
      }
    }

    const messageServeur = this.texteErreur(payloadBrut);
    if (messageServeur) return messageServeur;

    if (erreurHttp.status === 0) {
      return 'Le serveur Kompta est injoignable. Vérifiez sa connexion et la configuration réseau.';
    }

    const messageHttp = this.texteErreur(erreurHttp.message);
    return messageHttp
      ? `Impossible de récupérer les opérations depuis Powens : ${messageHttp}`
      : 'Impossible de récupérer les opérations depuis Powens.';
  }

  private texteErreur(valeur: unknown, profondeur = 0): string | null {
    if (profondeur > 5 || valeur === null || valeur === undefined) return null;
    if (typeof valeur === 'string') return valeur.trim() || null;
    if (typeof valeur === 'number' || typeof valeur === 'boolean') return String(valeur);
    if (Array.isArray(valeur)) {
      const textes = valeur
        .map((element) => this.texteErreur(element, profondeur + 1))
        .filter((texte): texte is string => Boolean(texte));
      return [...new Set(textes)].join(' ; ') || null;
    }
    if (typeof valeur !== 'object') return null;

    const objet = valeur as Record<string, unknown>;
    const champsPrioritaires = ['message', 'error_description', 'detail', 'description', 'errors', 'error'];
    const textesPrioritaires = champsPrioritaires
      .filter((champ) => champ in objet)
      .map((champ) => this.texteErreur(objet[champ], profondeur + 1))
      .filter((texte): texte is string => Boolean(texte));
    if (textesPrioritaires.length > 0) {
      return [...new Set(textesPrioritaires)].join(' ; ');
    }

    const textes = Object.entries(objet)
      .map(([champ, contenu]) => {
        const texte = this.texteErreur(contenu, profondeur + 1);
        return texte ? `${champ}: ${texte}` : null;
      })
      .filter((texte): texte is string => Boolean(texte));
    return textes.join(' ; ') || null;
  }

  private parserTSV(texte: string): LigneImportee[] {
    const lignesTexte = texte.trim().split('\n');
    if (lignesTexte.length < 2) return [];

    const entetes = lignesTexte[0].split('\t').map((h) => h.trim().toLowerCase());
    const idxDateValeur = entetes.findIndex((h) => h.includes('date') && h.includes('valeur'));
    const idxDate = entetes.findIndex((h) => h.includes('date') && !h.includes('valeur'));
    const idxLibelle = entetes.findIndex((h) => h.includes('libelle') || h.includes('libellé'));
    const idxLieu = entetes.findIndex((h) => h.includes('lieu'));
    const idxCategorie = entetes.findIndex((h) => h.includes('categ'));
    const idxType = entetes.findIndex((h) => h === 'type');
    const idxStatut = entetes.findIndex((h) => h.includes('statut'));
    const idxMontant = entetes.findIndex((h) => h.includes('montant'));

    if (idxDate < 0 || idxMontant < 0) return [];

    const result: LigneImportee[] = [];
    for (let i = 1; i < lignesTexte.length; i++) {
      const cols = lignesTexte[i].split('\t');
      if (cols.length < 2) continue;

      const dateStr = (cols[idxDate] ?? '').trim();
      const dateValeurStrBrute = (idxDateValeur >= 0 ? cols[idxDateValeur] ?? '' : '').trim();
      const libelleRaw = (idxLibelle >= 0 ? cols[idxLibelle] ?? '' : cols[1] ?? '').trim();
      const lieu = (idxLieu >= 0 ? cols[idxLieu] ?? '' : '').trim();
      const categorie = (idxCategorie >= 0 ? cols[idxCategorie] ?? '' : '').trim();
      const typeLabel = (idxType >= 0 ? cols[idxType] ?? '' : '').trim();
      const statut = (idxStatut >= 0 ? cols[idxStatut] ?? '' : '').trim();
      const montantStr = (cols[idxMontant] ?? '').trim();

      if (!dateStr || !montantStr) continue;

      const date = this.parseDateImport(dateStr);
      if (!date || isNaN(date.getTime())) continue;
      const dateValeur = dateValeurStrBrute ? this.parseDateImport(dateValeurStrBrute) : null;

      const montantBrut = this.parseMontantImport(montantStr);
      if (isNaN(montantBrut)) continue;

      const typeOp: 'ENTREE' | 'SORTIE' = montantBrut >= 0 ? 'ENTREE' : 'SORTIE';
      const moyenPaiementDetecte = this.detecterMoyenPaiement(typeLabel);
      const numeroCheque =
        moyenPaiementDetecte === 'CHEQUE' ? this.extraireNumeroCheque(libelleRaw) : null;
      // Clé normalisée : date ISO + libellé + montant en centimes (évite les variations d'encodage)
      const anneeRef = date.getFullYear();
      const moisRef = String(date.getMonth() + 1).padStart(2, '0');
      const jourRef = String(date.getDate()).padStart(2, '0');
      const referenceImport = `${anneeRef}-${moisRef}-${jourRef}|${libelleRaw}|${Math.round(montantBrut * 100)}`;

      result.push({
        dateStr,
        date,
        dateValeurStr: dateValeur ? this.formaterDateImport(dateValeur) : null,
        dateValeur,
        libelle: libelleRaw,
        lieu,
        categorie,
        typeLabel,
        statut,
        montantBrut,
        typeOp,
        moyenPaiementDetecte,
        numeroCheque,
        referenceImport,
      });
    }
    return result;
  }

  private parseDateImport(s: string): Date | null {
    const parts = s.split('/');
    if (parts.length !== 3) return null;
    const [d, m, y] = parts.map(Number);
    if (!d || !m || !y) return null;
    return new Date(y, m - 1, d);
  }

  private parseMontantImport(s: string): number {
    const clean = s.replace(/\s/g, '').replace('€', '').replace(',', '.');
    return parseFloat(clean);
  }

  private detecterMoyenPaiement(typeLabel: string): string | null {
    const t = typeLabel.toLowerCase();
    if (t.includes('chèque') || t.includes('cheque')) return 'CHEQUE';
    if (t.includes('virement')) return 'VIREMENT';
    if (t.includes('prélèvement') || t.includes('prelevement')) return 'PRELEVEMENT';
    if (t.includes('cb différé') || t.includes('cb differe') || t.includes('cb_differe')) return 'CB_DIFFERE';
    if (t.includes('cb') || t.includes('carte bancaire')) return 'CB';
    return null;
  }

  private extraireNumeroCheque(libelle: string): string | null {
    const m =
      libelle.match(/n[°o]?\s*\.+(\d+)/i) ??
      libelle.match(/(?:n[°o]|cheque)\s*(\d+)/i) ??
      libelle.match(/(\d{4,})$/);
    return m ? m[1] : null;
  }

  async onConfirmerImport(associations: AssociationConfirmee[]): Promise<void> {
    this.importLignes.set(null);
    this.importToutesLignes.set(null);
    if (associations.length === 0) return;

    this.erreur.set(null);
    let nbSucces = 0;

    for (const assoc of associations) {
      const annee = assoc.ligne.date.getFullYear();
      const mois = assoc.ligne.date.getMonth() + 1;
      const dateISO = this.formaterDateISO(assoc.ligne.date);
      const dateValeurISO = assoc.ligne.dateValeur
        ? this.formaterDateISO(assoc.ligne.dateValeur)
        : null;
      try {
        if (assoc.creerNouvelleOp) {
          const compteId = this.compte?.id;
          if (!compteId) throw new Error('Aucun compte sélectionné');
          const isVirement = assoc.ligne.moyenPaiementDetecte === 'VIREMENT';
          await firstValueFrom(
            this.comptesService.creerOperationReelle(compteId, {
              libelle: assoc.ligne.libelle,
              type: assoc.ligne.typeOp,
              montant: Math.abs(assoc.ligne.montantBrut),
              dateOperation: dateISO,
              dateValeur: dateValeurISO,
              estPointee: true,
              ...(assoc.ligne.categorie ? { categorie: assoc.ligne.categorie } : {}),
              ...(!isVirement && assoc.ligne.moyenPaiementDetecte ? { moyenPaiement: assoc.ligne.moyenPaiementDetecte } : {}),
              ...(assoc.ligne.numeroCheque ? { numeroCheque: assoc.ligne.numeroCheque } : {}),
              referenceImport: assoc.referenceImport,
            }),
          );
        } else {
          const isVirement = assoc.ligne.moyenPaiementDetecte === 'VIREMENT';
          await firstValueFrom(
            this.comptesService.pointerOperationPlanifiee(assoc.operationId!, annee, mois, {
              dateOperation: dateISO,
              dateValeur: dateValeurISO,
              montant: Math.abs(assoc.ligne.montantBrut),
              moyenPaiement: isVirement ? undefined : assoc.ligne.moyenPaiementDetecte,
              numeroCheque: assoc.ligne.numeroCheque ?? undefined,
              referenceImport: assoc.referenceImport,
              libelle: assoc.ligne.libelle,
            }),
          );
        }
        nbSucces++;
      } catch (err: unknown) {
        let detail = '';
        if (err && typeof err === 'object' && 'error' in err) {
          const e = (err as { error?: { message?: string; details?: string[] } }).error;
          if (e?.message) detail = ` : ${e.message}`;
          if (e?.details?.length) detail += ` (${e.details.join(', ')})`;
        }
        this.erreur.set(`Erreur lors du pointage de « ${assoc.ligne.libelle} »${detail}`);
      }
    }

    if (nbSucces > 0) {
      this.operationModifiee.emit();
      await this.chargerSilencieux();
    }
  }

  readonly aujourd = new Date();
  readonly moisCourant = this.aujourd.getMonth() + 1;
  readonly anneeCourante = this.aujourd.getFullYear();
  readonly moyensPaiement = MOYENS_PAIEMENT;

  readonly annee = signal(this.anneeCourante);
  readonly moisAffiche = signal(this.moisCourant);
  readonly libelleAnnee = computed(() =>
    this.annee() === this.anneeCourante + 1
      ? `${this.anneeCourante}–${this.annee()}`
      : `${this.annee()}`
  );
  readonly compteSignal = signal<Compte | null>(null);
  readonly tableau = signal<TableauPrevisionnel | null>(null);
  readonly chargement = signal(false);
  readonly erreur = signal<string | null>(null);

  // Édition du prévu et consultation des opérations passées
  readonly celluleEditeeKey = signal<string | null>(null);
  readonly celluleMontantEdite = signal<number | null>(0);

  // Pointage du mois sélectionné
  readonly pointageData = signal<{ op: OperationTableau; moisIdx: number } | null>(null);
  readonly enregistrementProvision = signal(false);

  // Édition opération (panel)
  readonly editionOp = signal<OperationTableau | null>(null);
  readonly editionForm = signal<EditionForm>({
    libelle: '', type: 'SORTIE', montant: 0, dateDebut: '', jourDuMois: 1,
    nombreDeMois: '', estEtalee: false, provision: false, categorie: '',
    moyenPaiement: null, numeroCheque: '', dateOperation: '', periodicite: 1,
  });
  readonly confirmSupprId = signal<number | null>(null);

  // Import presse-papier ou Powens
  readonly importLignes = signal<LigneImportee[] | null>(null);
  readonly importToutesLignes = signal<LigneImportee[] | null>(null);
  readonly importSource = signal('presse-papier');
  readonly chargementPowens = signal(false);
  readonly chargementGestionPowens = signal(false);
  readonly selectionPowensVisible = signal(false);
  readonly chargementSelectionPowens = signal(false);
  readonly erreurSelectionPowens = signal<string | null>(null);
  readonly comptesSelectionPowens = signal<ComptePowensDisponible[]>([]);
  readonly comptePrincipalSelectionPowensId = signal<number | null>(null);
  readonly comptesCarteSelectionPowensIds = signal<number[]>([]);

  // Filtres
  readonly masquerTerminees = signal(true);
  readonly masquerMoisPasses = signal(true);
  readonly masquerTermineesMoisCourant = signal(false);
  readonly masquerPointeesMobile = signal(true);

  readonly libelleMoisMobile = `${NOMS_MOIS[this.moisCourant - 1]} ${this.anneeCourante}`;

  readonly operationsMobile = computed(() => {
    const t = this.tableau();
    const idx = this.moisCourantIdx();
    if (!t || idx < 0) return [];
    return t.operations.filter((op) => {
      const pointe = this.montantPointePourMois(op, t.mois[idx]);
      const aPointer = this.montantAPointerMobile(op);
      const enAttente = this.aOperationsEnAttenteMobile(op);
      if (aPointer === 0 && pointe === 0 && !enAttente) return false;
      return !this.masquerPointeesMobile() || aPointer !== 0 || enAttente;
    }).sort((a, b) => a.jourDuMois - b.jourDuMois || a.libelle.localeCompare(b.libelle, 'fr'));
  });

  // Opérations groupées
  readonly entrees = computed(() => {
    const t = this.tableau();
    if (!t) return [];
    return t.operations
      .filter((op) => op.type === 'ENTREE')
      .sort((a, b) => +b.provision - +a.provision || a.jourDuMois - b.jourDuMois);
  });

  readonly sorties = computed(() => {
    const t = this.tableau();
    if (!t) return [];
    return t.operations
      .filter((op) => op.type === 'SORTIE')
      .sort((a, b) => +b.provision - +a.provision || a.jourDuMois - b.jourDuMois);
  });

  readonly entreesVisibles = computed(() => {
    let ops = this.entrees();
    if (this.masquerTerminees()) ops = ops.filter((op) => !this.opEstTerminee(op));
    if (this.masquerTermineesMoisCourant()) ops = ops.filter((op) => !this.opEstTermineeMoisCourant(op));
    return ops;
  });

  readonly sortiesVisibles = computed(() => {
    let ops = this.sorties();
    if (this.masquerTerminees()) ops = ops.filter((op) => !this.opEstTerminee(op));
    if (this.masquerTermineesMoisCourant()) ops = ops.filter((op) => !this.opEstTermineeMoisCourant(op));
    return ops;
  });

  readonly nombreMoisVisibles = computed(() => {
    const t = this.tableau();
    if (!t) return 0;
    const periodeInclutMoisCourant =
      this.annee() === this.anneeCourante ||
      this.annee() === this.anneeCourante + 1;
    if (!this.masquerMoisPasses() || !periodeInclutMoisCourant) return t.mois.length;
    return t.mois.filter((m) => !this.estMoisPasse(m)).length;
  });

  readonly moisCourantIdx = computed(() => {
    const t = this.tableau();
    if (!t) return -1;
    return t.mois.findIndex((m) => m.annee === this.anneeCourante && m.mois === this.moisCourant);
  });

  readonly moisAffichesAvecIdx = computed(() => {
    const t = this.tableau();
    if (!t) return [];
    const afficheAnneeSuivante = this.annee() === this.anneeCourante + 1;
    const anneeDebut = afficheAnneeSuivante ? this.anneeCourante : this.annee();
    const masqueMoisPassesApplicable =
      this.annee() === this.anneeCourante || afficheAnneeSuivante;
    const moisDebut = masqueMoisPassesApplicable && !this.masquerMoisPasses()
      ? 1
      : this.moisAffiche();
    const moisDebutIdx = t.mois.findIndex(
      (m) => m.annee === anneeDebut && m.mois === moisDebut
    );
    if (moisDebutIdx === -1) return [];
    
    return t.mois
      .slice(moisDebutIdx)
      .map((m, idx) => ({ mois: m, idx: moisDebutIdx + idx }));
  });

  readonly nombreMoisAffichesVue = computed(() => {
    return this.moisAffichesAvecIdx().length;
  });

  readonly totauxParMois = computed(() => {
    const t = this.tableau();
    if (!t) return [];
    return t.mois.map((_, idx) => {
      let entrees = 0;
      let sorties = 0;
      for (const op of t.operations) {
        const montant = this.montantAffiche(op, idx);
        if (montant === 0) continue;
        if (op.type === 'ENTREE') entrees += montant;
        else sorties += montant;
      }
      return { entrees, sorties, solde: entrees - sorties };
    });
  });

  readonly totalAnneeEntrees = computed(() =>
    this.entrees().reduce((sum, op) => sum + this.getTotal(op), 0)
  );

  readonly totalAnneeSorties = computed(() =>
    this.sorties().reduce((sum, op) => sum + this.getTotal(op), 0)
  );

  readonly totalAnneeSolde = computed(() =>
    this.totalAnneeEntrees() - this.totalAnneeSorties()
  );

  readonly soldesCumules = computed(() => {
    const totaux = this.totauxParMois();
    const idx = this.moisCourantIdx();
    const compte = this.compteSignal();
    let cumul = 0;
    return totaux.map((t, i) => {
      if (i === idx && idx !== -1 && compte) {
        // Mois courant : soldeDispo (solde compte + solde carte) + solde net mensuel
        cumul = Number(compte.soldeActuel ?? 0)
          + Number(compte.soldeCBDiffere ?? 0)
          + t.solde;
      } else {
        cumul += t.solde;
      }
      return cumul;
    });
  });

  readonly totauxCBDiffereParMois = computed(() => {
    const t = this.tableau();
    if (!t) return [];
    return t.mois.map((_, idx) => {
      let total = 0;
      for (const op of t.operations) {
        if (!this.estOperationCarteDifferee(op)) continue;
        total += this.montantAffiche(op, idx) * (op.type === 'SORTIE' ? 1 : -1);
      }
      return total;
    });
  });

  readonly soldesCumulesSansCBDifferee = computed(() => {
    const soldes = this.soldesCumules();
    const totauxCB = this.totauxCBDiffereParMois();
    const idx = this.moisCourantIdx();
    const compte = this.compteSignal();
    // Solde cumulé du mois, en retirant l'effet CB différé de ce même mois :
    // - mois courant : le solde CB différé du compte (déjà intégré dans soldesCumules) est retiré ;
    // - tous les mois : le montant des opérations CB différé de ce mois est réintégré (il avait été
    //   déduit du solde net mensuel).
    return soldes.map((solde, i) => {
      const soldeCarteCompte = i === idx && compte ? Number(compte.soldeCBDiffere ?? 0) : 0;
      return solde - soldeCarteCompte + (totauxCB[i] ?? 0);
    });
  });

  readonly totalAnneeCBDiffere = computed(() =>
    this.totauxCBDiffereParMois().reduce((s, montant) => s + montant, 0)
  );

  private estOperationCarteDifferee(operation: OperationTableau): boolean {
    return operation.moyenPaiement === 'CB_DIFFERE';
  }

  readonly joursPassageNegatif = computed<(number | null)[]>(() => {
    const t = this.tableau();
    const compte = this.compteSignal();
    if (!t || !compte) return [];

    const moisCourantIdx = this.moisCourantIdx();
    const premierMoisIdx = Math.max(moisCourantIdx, 0);
    const joursNegatifs = Array<number | null>(t.mois.length).fill(null);

    // Opérations "cash" (hors CB différé), appliquées directement au solde du mois où elles sont datées.
    const operationsCashParMois = t.mois.map(() => [] as { montant: number; jour: number }[]);
    // Mouvements CB différé : sorties négatives, entrées positives, reportés au mois suivant.
    const mouvementsCarteParMois = t.mois.map(() => 0);

    for (const operation of t.operations) {
      const estCarteDifferee = this.estOperationCarteDifferee(operation);
      for (let moisIdx = premierMoisIdx; moisIdx < t.mois.length; moisIdx++) {
        const montant = Number(operation.montantParMois[moisIdx] ?? 0);
        if (montant === 0) continue;
        const montantSigne = montant * (operation.type === 'ENTREE' ? 1 : -1);

        if (estCarteDifferee) {
          mouvementsCarteParMois[moisIdx] += montantSigne;
        } else {
          operationsCashParMois[moisIdx].push({ montant: montantSigne, jour: operation.jourDuMois });
        }
      }
    }

    let solde = Number(compte.soldeActuel ?? 0);
    // Le solde signé de la carte est reporté tel quel sur le compte au mois suivant.
    let soldeCarte = Number(compte.soldeCBDiffere ?? 0);

    for (let moisIdx = premierMoisIdx; moisIdx < t.mois.length; moisIdx++) {
      const operationsDuMois = [...operationsCashParMois[moisIdx]];

      // Aucun règlement carte sur le tout premier mois affiché.
      if (moisIdx > premierMoisIdx) {
        operationsDuMois.push({ montant: soldeCarte, jour: 4 });
        soldeCarte = 0;
      }
      operationsDuMois.sort((a, b) => a.jour - b.jour);

      let jourCroisement: number | null = null;
      // On applique toutes les opérations du mois (même après le passage en négatif)
      // pour que le solde reporté au mois suivant reste exact.
      for (const { montant, jour } of operationsDuMois) {
        // Réévalué avant chaque opération : une rentrée d'argent en cours de mois peut
        // repasser le compte au positif avant qu'un débit suivant ne le refasse plonger.
        const soldeAvant = solde;
        solde += montant;
        if (jourCroisement === null && soldeAvant >= 0 && solde < 0) {
          jourCroisement = jour;
        }
      }

      joursNegatifs[moisIdx] = jourCroisement;
      // Les mouvements carte de ce mois sont réglés le mois suivant.
      soldeCarte += mouvementsCarteParMois[moisIdx];
    }

    return joursNegatifs;
  });

  readonly soldeDispo = computed(() => {
    const compte = this.compteSignal();
    if (!compte) return null;
    return Number(compte.soldeActuel ?? 0) + Number(compte.soldeCBDiffere ?? 0);
  });

  readonly soldeSansCBDifferee = computed(() => {
    const compte = this.compteSignal();
    if (!compte) return null;
    return Number(compte.soldeActuel ?? 0);
  });

  constructor(private readonly comptesService: ComptesService) {}

  ngOnInit(): void {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    this.mediaTelephone = window.matchMedia('(max-width: 600px)');
    this.mediaTelephone.addEventListener('change', this.synchroniserVueTelephone);
    this.synchroniserVueTelephone();
  }

  ngOnDestroy(): void {
    this.mediaTelephone?.removeEventListener('change', this.synchroniserVueTelephone);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['compte']) {
      const prev = changes['compte'].previousValue as Compte | null;
      const curr = changes['compte'].currentValue as Compte | null;
      // Mettre à jour le signal à chaque changement (y compris soldeActuel / soldeCBDiffere)
      this.compteSignal.set(curr);
      // Ne recharger le tableau que si le compte change réellement (pas juste une nouvelle référence d'objet)
      if (prev?.id !== curr?.id) {
        this.rechercheVisible.set(false);
        this.selectionPowensVisible.set(false);
        this.comptesSelectionPowens.set([]);
        this.comptePrincipalSelectionPowensId.set(null);
        this.comptesCarteSelectionPowensIds.set([]);
        void this.charger();
      }
    }
    if (changes['refreshToken'] && !changes['refreshToken'].firstChange) {
      void this.chargerSilencieux();
    }
  }

  async charger(): Promise<void> {
    if (!this.compte) {
      this.tableau.set(null);
      return;
    }
    this.chargement.set(true);
    this.erreur.set(null);
    try {
      const { anneeDebut, nombreMois } = this.parametresChargement();
      const data = await firstValueFrom(
        this.comptesService.listerOperationsTableau(
          this.compte.id,
          anneeDebut,
          1,
          nombreMois
        )
      );
      this.tableau.set(data);
    } catch {
      this.erreur.set('Impossible de charger le plan annuel.');
    } finally {
      this.chargement.set(false);
    }
  }

  private async chargerSilencieux(): Promise<void> {
    if (!this.compte) return;
    try {
      const { anneeDebut, nombreMois } = this.parametresChargement();
      const data = await firstValueFrom(
        this.comptesService.listerOperationsTableau(
          this.compte.id,
          anneeDebut,
          1,
          nombreMois
        )
      );
      this.tableau.set(data);
    } catch {
      this.erreur.set('Impossible de rafraîchir les données.');
    }
  }

  private parametresChargement(): { anneeDebut: number; nombreMois: number } {
    if (this.annee() === this.anneeCourante + 1) {
      return { anneeDebut: this.anneeCourante, nombreMois: 24 };
    }
    return { anneeDebut: this.annee(), nombreMois: 12 };
  }

  anneePrecedente(): void {
    this.annee.update((a) => a - 1);
    this.reinitialiserMoisAffiche();
    void this.charger();
  }

  anneeSuivante(): void {
    this.annee.update((a) => a + 1);
    this.reinitialiserMoisAffiche();
    void this.charger();
  }

  private reinitialiserMoisAffiche(): void {
    this.moisAffiche.set(
      this.annee() === this.anneeCourante ||
      this.annee() === this.anneeCourante + 1
        ? this.moisCourant
        : 1
    );
  }

  moisPrecedent(): void {
    const moisActuel = this.moisAffiche();
    if (moisActuel === 1) {
      // Aller au mois précédent de l'année précédente
      this.annee.update((a) => a - 1);
      this.moisAffiche.set(12);
    } else {
      this.moisAffiche.update((m) => m - 1);
    }
       void this.charger();
  }

  estMoisCourant(m: { annee: number; mois: number }): boolean {
    return m.annee === this.anneeCourante && m.mois === this.moisCourant;
  }

  estMoisPrecedent(m: { annee: number; mois: number }): boolean {
    const moisPrecedent = new Date(this.anneeCourante, this.moisCourant - 2, 1);
    return m.annee === moisPrecedent.getFullYear() && m.mois === moisPrecedent.getMonth() + 1;
  }

  estMoisPointable(m: { annee: number; mois: number }): boolean {
    return this.estMoisCourant(m) || this.estMoisPrecedent(m);
  }

  estMoisPasse(m: { annee: number; mois: number }): boolean {
    return (
      m.annee < this.anneeCourante ||
      (m.annee === this.anneeCourante && m.mois < this.moisCourant)
    );
  }

  estMoisFutur(m: { annee: number; mois: number }): boolean {
    return (
      m.annee > this.anneeCourante ||
      (m.annee === this.anneeCourante && m.mois > this.moisCourant)
    );
  }

  nomMois(m: { annee: number; mois: number }): string {
    const plusieursAnnees = new Set(
      this.moisAffichesAvecIdx().map(({ mois }) => mois.annee)
    ).size > 1;
    return `${NOMS_MOIS[m.mois - 1]}${plusieursAnnees ? ` ${m.annee}` : ''}`;
  }

  formatterMontant(montant: number): string {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 2,
    }).format(montant);
  }

  celluleKey(opId: number, moisIdx: number): string {
    return `${opId}-${moisIdx}`;
  }

  hasOverride(op: OperationTableau, moisIdx: number): boolean {
    const t = this.tableau();
    if (!t || op.estReelle) return false;
    const m = t.mois[moisIdx];
    return op.overrides?.some((o) => o.annee === m.annee && o.mois === m.mois) ?? false;
  }

  getTotal(op: OperationTableau): number {
    const t = this.tableau();
    if (!t) return 0;
    return t.mois.reduce((sum, mois, idx) => {
      // Le pointé des mois passés est déjà inclus dans leur cellule.
      const pointeSepare = op.estReelle && !this.estMoisPasse(mois)
        ? this.montantPointePourMois(op, mois)
        : 0;
      return sum + this.montantAffiche(op, idx) + pointeSepare;
    }, 0);
  }

  montantPointePourMois(op: OperationTableau, mois: { annee: number; mois: number }): number {
    return totalOpsReelles(
      filtrerOperationsReellesParMois(op.operationsReelles ?? [], mois.annee, mois.mois)
        .filter((reelle) => reelle.estPointee)
    );
  }

  montantPointeCeMois(op: OperationTableau): number {
    if (this.annee() !== this.anneeCourante) return 0;
    return (op.operationsReelles ?? [])
      .filter((r) => {
        const anneeOp = parseInt(r.dateOperation.substring(0, 4), 10);
        const moisOp = parseInt(r.dateOperation.substring(5, 7), 10);
        return r.estPointee && anneeOp === this.anneeCourante && moisOp === this.moisCourant;
      })
      .reduce((sum, r) => sum + Number(r.montant), 0);
  }

  montantAPointerMobile(op: OperationTableau): number {
    const idx = this.moisCourantIdx();
    if (idx < 0 || (op.montantParMois[idx] ?? 0) === 0) return 0;
    return this.montantAffiche(op, idx);
  }

  aOperationsEnAttenteMobile(op: OperationTableau): boolean {
    if (!op.provision || op.estReelle) return false;
    const moisCourant = `${this.anneeCourante}-${String(this.moisCourant).padStart(2, '0')}`;
    return (op.operationsReelles ?? []).some((reelle) =>
      !reelle.estPointee && reelle.dateOperation.substring(0, 7) <= moisCourant
    );
  }

  numerosChequeCeMois(op: OperationTableau): string[] {
    if (op.estReelle || this.annee() !== this.anneeCourante) return [];
    return (op.operationsReelles ?? [])
      .filter((r) => {
        const anneeOp = parseInt(r.dateOperation.substring(0, 4), 10);
        const moisOp = parseInt(r.dateOperation.substring(5, 7), 10);
        return r.estPointee && r.moyenPaiement === 'CHEQUE' && r.numeroCheque
          && anneeOp === this.anneeCourante && moisOp === this.moisCourant;
      })
      .map((r) => r.numeroCheque!);
  }

  echeanceCourante(op: OperationTableau): { n: number; total: number; numeroCheque: string | null } | null {
    const idx = this.moisCourantIdx();
    if (idx < 0 || !op.echeancesParMois) return null;
    return op.echeancesParMois[idx] ?? null;
  }

  totalPointeCeMoisEntrees(): number {
    const t = this.tableau();
    if (!t || this.annee() !== this.anneeCourante) return 0;
    return t.operations
      .filter((op) => op.type === 'ENTREE')
      .reduce((sum, op) => sum + this.montantPointeCeMois(op), 0);
  }

  totalPointeCeMoisSorties(): number {
    const t = this.tableau();
    if (!t || this.annee() !== this.anneeCourante) return 0;
    return t.operations
      .filter((op) => op.type === 'SORTIE')
      .reduce((sum, op) => sum + this.montantPointeCeMois(op), 0);
  }

  totalPointeCeMoisCBDifferee(): number {
    const t = this.tableau();
    if (!t || this.annee() !== this.anneeCourante) return 0;
    return t.operations
      .filter((op) => this.estOperationCarteDifferee(op))
      .reduce((sum, op) => sum + this.montantPointeCeMois(op) * (op.type === 'SORTIE' ? 1 : -1), 0);
  }

  // ── Édition cellule ─────────────────────────────────────────────────────────

  celluleEditable(op: OperationTableau, m: { annee: number; mois: number }): boolean {
    if (this.estMoisPasse(m)) return op.provision || this.montantPointePourMois(op, m) > 0;
    return !op.estReelle;
  }

  ouvrirEditionCellule(op: OperationTableau, moisIdx: number): void {
    const t = this.tableau();
    if (!t) return;
    if (!this.celluleEditable(op, t.mois[moisIdx])) return;
    if (this.estMoisPasse(t.mois[moisIdx])) {
      this.ouvrirPointage(op, moisIdx);
      return;
    }
    this.celluleEditeeKey.set(this.celluleKey(op.id, moisIdx));
    this.celluleMontantEdite.set(op.montantParMois[moisIdx] ?? 0);
  }

  annulerEditionCellule(): void {
    this.celluleEditeeKey.set(null);
  }

  saisirMontantCellule(montant: number | null): void {
    this.celluleMontantEdite.set(montant);
  }

  async sauvegarderCellule(op: OperationTableau, moisIdx: number): Promise<void> {
    const t = this.tableau();
    if (!t) return;
    const m = t.mois[moisIdx];
    const montant = this.celluleMontantEdite();
    if (montant === null || !Number.isFinite(montant) || montant < 0) {
      this.erreur.set('Saisissez un montant ou un calcul valide, supérieur ou égal à zéro.');
      return;
    }
    this.erreur.set(null);
    try {
      await firstValueFrom(
        this.comptesService.definirOverrideMois(op.id, m.annee, m.mois, montant)
      );
      this.celluleEditeeKey.set(null);
      await this.chargerSilencieux();
    } catch {
      this.erreur.set('Erreur lors de la sauvegarde du montant.');
    }
  }

  async supprimerOverride(op: OperationTableau, moisIdx: number): Promise<void> {
    const t = this.tableau();
    if (!t) return;
    const m = t.mois[moisIdx];
    this.erreur.set(null);
    try {
      await firstValueFrom(
        this.comptesService.supprimerOverrideMois(op.id, m.annee, m.mois)
      );
      await this.chargerSilencieux();
    } catch {
      this.erreur.set("Erreur lors de la suppression de l'override.");
    }
  }

  // ── Pointage mois courant ────────────────────────────────────────────────────

  ouvrirPointage(op: OperationTableau, moisIdx: number): void {
    this.erreur.set(null);
    this.pointageData.set({ op, moisIdx });
  }

  annulerPointage(): void {
    this.pointageData.set(null);
  }

  async onDepointerOperation(reelleId: number): Promise<void> {
    if (this.enregistrementProvision()) return;
    this.enregistrementProvision.set(true);
    this.erreur.set(null);
    try {
      if (this.pointageData()?.op.provision || this.pointageData()?.op.estReelle) {
        await firstValueFrom(this.comptesService.depointerOperationReelle(reelleId));
      } else {
        await firstValueFrom(this.comptesService.supprimerOperationReelle(reelleId));
      }
      this.operationModifiee.emit();
      await this.chargerSilencieux();
      // Resynchroniser l'op en mémoire après rechargement
      const data = this.pointageData();
      if (data) {
        const opFraiche = this.tableau()?.operations.find((o) => o.id === data.op.id && o.estReelle === data.op.estReelle);
        if (opFraiche) {
          this.pointageData.set({ op: opFraiche, moisIdx: data.moisIdx });
        } else {
          this.pointageData.set(null);
        }
      }
    } catch {
      this.erreur.set('Erreur lors du dépointage.');
    } finally {
      this.enregistrementProvision.set(false);
    }
  }

  async onAjouterOperationProvision(payload: AjoutOperationProvisionPayload): Promise<void> {
    const data = this.pointageData();
    if (!data?.op.provision || !this.compte || this.enregistrementProvision()) return;
    this.enregistrementProvision.set(true);
    this.erreur.set(null);
    try {
      const reelle = await firstValueFrom(this.comptesService.creerOperationReelle(this.compte.id, {
        ...payload,
        type: data.op.type,
        operationPlanifieeId: data.op.id,
        categorie: data.op.categorie ?? undefined,
        estPointee: false,
      }));
      this.pointageData.set({ ...data, op: {
        ...data.op, operationsReelles: [...(data.op.operationsReelles ?? []), reelle],
      } });
      await this.rafraichirOperationsEnPointage();
    } catch {
      this.erreur.set("Impossible d'ajouter l'opération non pointée à la provision.");
    } finally {
      this.enregistrementProvision.set(false);
    }
  }

  async onPointerOperationProvision(reelleId: number): Promise<void> {
    const data = this.pointageData();
    if (!data?.op.provision || this.enregistrementProvision() ||
      !this.operationsNonPointeesPourPointage().some((op) => op.id === reelleId)) return;
    this.enregistrementProvision.set(true);
    this.erreur.set(null);
    try {
      await firstValueFrom(this.comptesService.pointerOperationReelle(reelleId));
      this.pointageData.set({ ...data, op: {
        ...data.op,
        operationsReelles: data.op.operationsReelles?.map((op) =>
          op.id === reelleId ? { ...op, estPointee: true } : op),
      } });
      await this.rafraichirOperationsEnPointage();
    } catch {
      this.erreur.set("Impossible de pointer l'opération liée à la provision.");
    } finally {
      this.enregistrementProvision.set(false);
    }
  }

  async onReporterOperationProvision(reelleId: number): Promise<void> {
    const data = this.pointageData();
    const periode = data && this.tableau()?.mois[data.moisIdx];
    if (!data?.op.provision || !periode || this.enregistrementProvision()) return;
    const reelle = this.operationsNonPointeesPourPointage().find((op) => op.id === reelleId);
    if (!reelle) return;
    const { annee, mois } = periode;
    const prochainMois = new Date(annee, mois, 1);
    const dernierJour = new Date(annee, mois + 1, 0).getDate();
    const jour = Math.min(Number(reelle.dateOperation.substring(8, 10)), dernierJour);
    const dateOperation = `${prochainMois.getFullYear()}-${String(prochainMois.getMonth() + 1).padStart(2, '0')}-${String(jour).padStart(2, '0')}`;
    this.enregistrementProvision.set(true);
    this.erreur.set(null);
    try {
      await firstValueFrom(this.comptesService.mettreAJourOperationReelle(reelleId, { dateOperation }));
      this.pointageData.set({ ...data, op: {
        ...data.op,
        operationsReelles: data.op.operationsReelles?.map((op) =>
          op.id === reelleId ? { ...op, dateOperation } : op),
      } });
      await this.rafraichirOperationsEnPointage();
    } catch {
      this.erreur.set("Impossible de reporter l'opération au mois suivant.");
    } finally {
      this.enregistrementProvision.set(false);
    }
  }

  async onSupprimerOperationProvision(reelleId: number): Promise<void> {
    const data = this.pointageData();
    if (!data?.op.provision || this.enregistrementProvision() ||
      !this.operationsNonPointeesPourPointage().some((op) => op.id === reelleId)) return;
    this.enregistrementProvision.set(true);
    this.erreur.set(null);
    try {
      await firstValueFrom(this.comptesService.supprimerOperationReelle(reelleId));
      this.pointageData.set({ ...data, op: {
        ...data.op, operationsReelles: data.op.operationsReelles?.filter((op) => op.id !== reelleId),
      } });
      await this.rafraichirOperationsEnPointage();
    } catch {
      this.erreur.set("Impossible de supprimer l'opération non pointée liée à la provision.");
    } finally {
      this.enregistrementProvision.set(false);
    }
  }

  private async rafraichirOperationsEnPointage(): Promise<void> {
    this.operationModifiee.emit();
    await this.chargerSilencieux();
    const data = this.pointageData();
    if (!data) return;
    const op = this.tableau()?.operations.find((operation) => operation.id === data.op.id && operation.estReelle === data.op.estReelle);
    this.pointageData.set(op ? { ...data, op } : null);
  }

  async supprimerPointagesCeMois(op: OperationTableau): Promise<void> {
    const ids = (op.operationsReelles ?? [])
      .filter((r) => {
        const anneeOp = parseInt(r.dateOperation.substring(0, 4), 10);
        const moisOp = parseInt(r.dateOperation.substring(5, 7), 10);
        return r.estPointee && anneeOp === this.anneeCourante && moisOp === this.moisCourant;
      })
      .map((r) => r.id);
    if (ids.length === 0) return;
    this.erreur.set(null);
    try {
      for (const id of ids) {
        await firstValueFrom(this.comptesService.supprimerOperationReelle(id));
      }
      this.operationModifiee.emit();
      await this.chargerSilencieux();
    } catch {
      this.erreur.set('Erreur lors du dépointage.');
    }
  }

  async onConfirmerPointage(payload: PointageConfirmPayload): Promise<void> {
    const data = this.pointageData();
    const t = this.tableau();
    if (!data || !t) return;
    const { op, moisIdx } = data;
    const m = t.mois[moisIdx];
    this.erreur.set(null);
    try {
      if (op.estReelle) {
        await firstValueFrom(this.comptesService.pointerOperationReelle(op.id));
        this.pointageData.set(null);
      } else {
        const operationReelleCreee = await firstValueFrom(
          this.comptesService.pointerOperationPlanifiee(op.id, m.annee, m.mois, {
            montant: payload.montant,
            moyenPaiement: payload.moyenPaiement,
            numeroCheque: payload.numeroCheque ?? undefined,
            compteDestinationId: payload.compteDestinationId,
          })
        );
        if (op.provision && operationReelleCreee) {
          // Injecter la nouvelle opération réelle dans l'op pour que l'historique s'actualise
          op.operationsReelles = [...(op.operationsReelles ?? []), operationReelleCreee];
        }
        if (!payload.continuer) {
          this.pointageData.set(null);
        }
      }
      this.operationModifiee.emit();
      await this.chargerSilencieux();
      // Après rechargement, resynchroniser l'op en mémoire avec la version fraîche du tableau
      if (payload.continuer && !op.estReelle) {
        const nouveauTableau = this.tableau();
        const opFraiche = nouveauTableau?.operations.find((o) => o.id === op.id);
        if (opFraiche) {
          this.pointageData.set({ op: opFraiche, moisIdx });
        }
      }
    } catch {
      this.erreur.set('Erreur lors du pointage.');
    }
  }

  async onSauvegarderEditionPlanifiee(payload: EditionPlanifieePayload): Promise<void> {
    const op = this.editionOp();
    if (!op) return;
    this.erreur.set(null);
    try {
      const p: Partial<CreationOperationPlanifiee> = {
        libelle: payload.libelle,
        type: payload.type as 'ENTREE' | 'SORTIE',
        montant: payload.montant,
        dateDebut: payload.dateDebut,
        jourDuMois: payload.jourDuMois,
        nombreDeMois: payload.nombreDeMois ?? null,
        estEtalee: payload.estEtalee,
        ...(payload.estEtalee && payload.nombreMensualites
          ? { nombreMensualites: payload.nombreMensualites }
          : {}),
        ...(payload.estEtalee && payload.montantInitial
          ? { montantInitial: payload.montantInitial }
          : {}),
        provision: payload.provision,
        categorie: payload.categorie || undefined,
        moyenPaiement: payload.moyenPaiement ?? null,
        numeroCheque: payload.numeroCheque,
        periodicite: payload.periodicite,
        virementProgramme: payload.virementProgramme,
        ...(payload.moyenPaiement === 'VIREMENT'
          ? {
              compteDestinationId: payload.compteDestinationId ?? undefined,
              compteDestinationExterne: payload.compteDestinationExterne ?? undefined,
            }
          : {}),
      };
      await firstValueFrom(this.comptesService.mettreAJourOperationPlanifiee(op.id, p));
      this.editionOp.set(null);
      this.operationModifiee.emit();
      await this.chargerSilencieux();
    } catch (error: unknown) {
      this.erreur.set(this.messageErreurGenerique(error, 'Erreur lors de la sauvegarde.'));
    }
  }

  async onSupprimerEditionPlanifiee(id: number): Promise<void> {
    this.erreur.set(null);
    try {
      await firstValueFrom(this.comptesService.supprimerOperationPlanifiee(id));
      this.editionOp.set(null);
      this.confirmSupprId.set(null);
      this.operationModifiee.emit();
      await this.chargerSilencieux();
    } catch {
      this.erreur.set('Erreur lors de la suppression.');
    }
  }

  // ── Édition opération ───────────────────────────────────────────────────────

  ouvrirEditionOp(op: OperationTableau): void {
    this.erreur.set(null);
    this.editionOp.set(op);
    this.confirmSupprId.set(null);
    this.editionForm.set({
      libelle: op.libelle,
      type: op.type,
      montant: op.montant,
      dateDebut: op.dateDebut ?? '',
      jourDuMois: op.jourDuMois,
      nombreDeMois: op.nombreDeMois ?? '',
      estEtalee: op.estEtalee,
      provision: op.provision,
      categorie: op.categorie ?? '',
      moyenPaiement: op.moyenPaiement ?? null,
      numeroCheque: op.numeroCheque ?? '',
      dateOperation: op.estReelle ? (op.dateDebut ?? '') : '',
      periodicite: op.periodicite ?? 1,
    });
  }

  ouvrirEditionOperationReelle(reelleId: number): void {
    const data = this.pointageData();
    const reelle = data?.op.operationsReelles?.find((op) => op.id === reelleId);
    if (!data || !reelle || this.enregistrementProvision()) return;
    this.ouvrirEditionOp({
      ...data.op,
      ...reelle,
      type: reelle.type ?? data.op.type,
      categorie: reelle.categorie ?? null,
      dateDebut: reelle.dateOperation,
      jourDuMois: Number(reelle.dateOperation.substring(8, 10)),
      estReelle: true,
      provision: false,
      groupeIds: null,
      groupeRef: null,
    });
  }

  annulerEditionOp(): void {
    this.editionOp.set(null);
    this.confirmSupprId.set(null);
  }

  updateEditionForm(field: string, value: unknown): void {
    this.editionForm.update((f) => ({ ...f, [field]: value }));
  }

  async sauvegarderEditionOp(): Promise<void> {
    const op = this.editionOp();
    const form = this.editionForm();
    if (!op) return;
    if (!Number.isFinite(form.montant)) {
      this.erreur.set('Saisissez un montant ou un calcul valide.');
      return;
    }
    this.erreur.set(null);
    const numeroCheque = form.moyenPaiement === 'CHEQUE' ? form.numeroCheque.trim() : null;
    if (form.moyenPaiement === 'CHEQUE' && !numeroCheque) {
      this.erreur.set('Le numéro de chèque est obligatoire.');
      return;
    }
    try {
      if (op.estReelle) {
        const payload: Partial<CreationOperationReelle> = {
          libelle: form.libelle,
          type: form.type,
          montant: Number(form.montant),
          dateOperation: form.dateOperation,
          categorie: form.categorie || undefined,
          moyenPaiement: form.moyenPaiement ?? null,
          numeroCheque,
        };
        await firstValueFrom(this.comptesService.mettreAJourOperationReelle(op.id, payload));
      } else {
        const payload: Partial<CreationOperationPlanifiee> = {
          libelle: form.libelle,
          type: form.type,
          montant: Number(form.montant),
          dateDebut: form.dateDebut,
          jourDuMois: Number(form.jourDuMois),
          nombreDeMois: form.nombreDeMois !== '' ? Number(form.nombreDeMois) : null,
          estEtalee: form.estEtalee,
          provision: form.provision,
          categorie: form.categorie || undefined,
          moyenPaiement: form.moyenPaiement ?? null,
          numeroCheque,
          periodicite: Number(form.periodicite ?? 1),
        };
        await firstValueFrom(this.comptesService.mettreAJourOperationPlanifiee(op.id, payload));
      }
      this.editionOp.set(null);
      await this.rafraichirOperationsEnPointage();
    } catch (error: unknown) {
      this.erreur.set(this.messageErreurGenerique(error, 'Erreur lors de la sauvegarde.'));
    }
  }

  demanderConfirmSuppression(): void {
    const op = this.editionOp();
    if (op) this.confirmSupprId.set(op.id);
  }

  annulerSuppression(): void {
    this.confirmSupprId.set(null);
  }

  async supprimerOp(): Promise<void> {
    const op = this.editionOp();
    if (!op) return;
    this.erreur.set(null);
    try {
      if (op.estReelle) {
        await firstValueFrom(this.comptesService.supprimerOperationReelle(op.id));
      } else {
        await firstValueFrom(this.comptesService.supprimerOperationPlanifiee(op.id));
      }
      this.editionOp.set(null);
      this.confirmSupprId.set(null);
      await this.rafraichirOperationsEnPointage();
    } catch {
      this.erreur.set('Erreur lors de la suppression.');
    }
  }

  async supprimerOpGroupe(): Promise<void> {
    const op = this.editionOp();
    if (!op) return;
    this.erreur.set(null);
    try {
      if (op.groupeRef) {
        await firstValueFrom(this.comptesService.supprimerGroupePlanifiees(op.groupeRef));
      } else if (op.groupeIds && op.groupeIds.length > 0) {
        await firstValueFrom(this.comptesService.supprimerMultiplePlanifiees(op.groupeIds));
      } else {
        return;
      }
      this.editionOp.set(null);
      this.confirmSupprId.set(null);
      this.operationModifiee.emit();
      await this.chargerSilencieux();
    } catch {
      this.erreur.set('Erreur lors de la suppression du groupe.');
    }
  }

  async pointerSansModif(op: OperationTableau, moisIdx: number): Promise<void> {
    const t = this.tableau();
    if (!t) return;
    const m = t.mois[moisIdx];
    this.erreur.set(null);
    try {
      if (op.estReelle) {
        await firstValueFrom(this.comptesService.pointerOperationReelle(op.id));
      } else {
        await firstValueFrom(
          this.comptesService.pointerOperationPlanifiee(op.id, m.annee, m.mois, {
            montant: op.montantParMois[moisIdx] ?? op.montant,
            moyenPaiement: op.moyenPaiement,
          })
        );
      }
      this.operationModifiee.emit();
      await this.chargerSilencieux();
    } catch {
      this.erreur.set('Erreur lors du pointage.');
    }
  }

  trackById(_: number, op: OperationTableau): number {
    return op.id;
  }

  /** Retourne true si le groupe d'ops réelles a au moins une op non pointée pour le mois donné. */
  hasUnpointedReellesCeMois(op: OperationTableau, moisIdx: number): boolean {
    const t = this.tableau();
    if (!t) return false;
    const m = t.mois[moisIdx];
    return filtrerOperationsReellesParMois(op.operationsReelles ?? [], m.annee, m.mois)
      .some((r) => !r.estPointee);
  }

  operationsReellesPourPointage(): OperationReelle[] {
    const data = this.pointageData();
    const t = this.tableau();
    if (!data || !t) return [];
    const m = t.mois[data.moisIdx];
    return filtrerOperationsReellesParMois(data.op.operationsReelles ?? [], m.annee, m.mois)
      .filter((r) => r.estPointee);
  }

  totalReellesPourPointage(): number {
    return totalOpsReelles(this.operationsReellesPourPointage());
  }

  operationsNonPointeesPourPointage(): OperationReelle[] {
    const data = this.pointageData();
    const periode = data && this.tableau()?.mois[data.moisIdx];
    if (!data || !periode) return [];
    const moisAffiche = `${periode.annee}-${String(periode.mois).padStart(2, '0')}`;
    return (data.op.operationsReelles ?? [])
      .filter((op) => !op.estPointee && op.dateOperation.substring(0, 7) <= moisAffiche)
      .sort((a, b) => b.dateOperation.localeCompare(a.dateOperation));
  }

  /**
   * Retourne le montant à afficher dans une cellule.
   * Pour les provisions au mois courant : valeurRestante (ce qui reste à utiliser).
   * Pour les mois passés : uniquement les opérations réellement pointées.
   */
  montantAffiche(op: OperationTableau, moisIdx: number): number {
    const t = this.tableau();
    if (!t) return 0;
    const m = t.mois[moisIdx];
    if (this.estMoisPasse(m)) return this.montantPointePourMois(op, m);
    if (op.provision && !op.estReelle && this.estMoisCourant(m)) {
      return op.valeurRestante !== null && op.valeurRestante !== undefined
        ? op.valeurRestante
        : op.montant;
    }
    return op.montantParMois[moisIdx] ?? 0;
  }

  montantInitialPointage(): number {
    const data = this.pointageData();
    const mois = data && this.tableau()?.mois[data.moisIdx];
    if (!data || !mois) return 0;
    if (this.estMoisPasse(mois)) {
      const budget = data.op.montantParMois[data.moisIdx] ?? 0;
      return data.op.provision
        ? Math.max(0, budget - this.montantPointePourMois(data.op, mois))
        : budget;
    }
    return this.montantAffiche(data.op, data.moisIdx);
  }

  libellePeriodePointage(): string {
    const data = this.pointageData();
    const mois = data && this.tableau()?.mois[data.moisIdx];
    return mois ? `${NOMS_MOIS[mois.mois - 1]} ${mois.annee}` : '';
  }

  private aHistoriqueVisible(op: OperationTableau): boolean {
    return this.moisAffichesAvecIdx().some(({ mois }) =>
      this.estMoisPasse(mois) && this.montantPointePourMois(op, mois) > 0
    );
  }

  /** Retourne true si l'opération n'a aucun montant pour le mois courant. */
  opEstTermineeMoisCourant(op: OperationTableau): boolean {
    if (this.aHistoriqueVisible(op)) return false;
    if (op.provision && op.operationsReelles?.some((reelle) => !reelle.estPointee)) return false;
    const t = this.tableau();
    if (!t || this.annee() !== this.anneeCourante) return false;
    const idx = t.mois.findIndex((m) => m.annee === this.anneeCourante && m.mois === this.moisCourant);
    if (idx === -1) return false;
    return (op.montantParMois[idx] ?? 0) === 0;
  }

  /** Retourne true si l'opération n'a plus aucun montant dans les mois courant et futurs,
   *  ET n'a pas de montant déjà pointé sur le mois courant. */
  opEstTerminee(op: OperationTableau): boolean {
    if (this.aHistoriqueVisible(op)) return false;
    if (op.provision && op.operationsReelles?.some((reelle) => !reelle.estPointee)) return false;
    const t = this.tableau();
    if (!t) return false;
    // Pour une année entièrement passée, ne rien masquer (l'utilisateur consulte l'historique)
    if (this.annee() < this.anneeCourante) return false;
    // Si l'opération a des montants pointés ce mois-ci, elle reste visible
    if (this.montantPointeCeMois(op) !== 0) return false;
    return t.mois.every((m, idx) => {
      if (this.estMoisPasse(m)) return true; // les mois passés ne comptent pas
      return (op.montantParMois[idx] ?? 0) === 0;
    });
  }
}
